/**
 * The clip on the valkokangas, and the moment the hall reacts to it.
 *
 * A projector at the back of the juhlasali, still running somebody's demo from
 * last night, thrown on the screen as an unlit plane: `MeshBasicMaterial` takes
 * no light, which is what makes a rectangle read as projected rather than as
 * painted on. Everything here is presentation - the simulation does not know
 * the hall has a screen in it, and could not be told.
 *
 * Two things are worth being deliberate about.
 *
 * The clip is *wall clock*, not simulation time. It has been playing since the
 * page loaded, it keeps playing while you are reading the READY screen, and it
 * does not restart because somebody pressed R. A projector that rewound itself
 * every attempt would be a projector that is really a cutscene.
 *
 * The beam is built from the same rectangle as the picture and hung off the
 * picture's own mesh, so the two cannot come apart: the light in the air is
 * literally the light on the wall, it turns with it, and it goes out with it
 * when somebody pulls the cable off the tolppa.
 *
 * The cue - the shout that lands fifteen seconds in - is derived by
 * *crossing* that timestamp rather than by matching it. At 240 Hz a frame is
 * four milliseconds and at 30 Hz it is thirty-three, `currentTime` moves in
 * whatever steps the decoder feels like, and a test for equality would fire on
 * some machines and not on others. See `crossedCue`, which is the whole of the
 * timing and is pure.
 *
 * And the picture is expected to keep running without anybody asking it to.
 * Three things can stop it and none of them used to be recovered from:
 *
 *   - autoplay refused, which is the one that was handled: the first thing the
 *     player does to the page is taken as permission to try once more.
 *   - a load that never arrives. A muted `play()` on a source the browser has
 *     accepted resolves happily and then sits at `readyState` 0 for ever, so
 *     the promise says nothing; what says something is `error`, or `stalled`
 *     with not one frame of metadata behind it. Both are started again, up to
 *     `MAX_RELOADS`.
 *   - a pause nobody here asked for. `pause` is fired by `sync()` on purpose
 *     and by the browser for its own reasons - an interruption, a media
 *     session taken by something else - and the second kind would otherwise
 *     leave one still frame on the wall for the rest of the session. A still
 *     frame is exactly what "the projector is broken" looks like, so an
 *     unasked-for pause is simply undone.
 *
 * None of it is retried for ever. A source that is genuinely not going to
 * arrive leaves the screen dark, which is a better failure than a tab that
 * spends the session reloading it.
 */

import * as THREE from 'three';
import type { BeamSpec, ProjectionSpec } from '../world/Level';
import clipUrl from './video/valkokangas.mp4';

/**
 * Did the playhead pass `cueAt` between these two frames?
 *
 * `current < previous` means the clip looped in between, so the stretch it
 * covered is the tail of the clip *and* the head of the next time round - and
 * the cue is in it if it is in either.
 */
export function crossedCue(previous: number, current: number, cueAt: number): boolean {
  if (current >= previous) return previous < cueAt && current >= cueAt;
  return previous < cueAt || current >= cueAt;
}

/** Events the page fires that count as "the user has arrived", for autoplay. */
const UNLOCK_EVENTS = ['pointerdown', 'keydown', 'touchstart'] as const;

/**
 * How many times a clip that is not arriving is started again.
 *
 * The budget is refilled the moment frames start flowing (`playing`), so this
 * bounds one bad patch rather than the session: three failures in a row with
 * nothing in between is a source that is not coming back.
 */
const MAX_RELOADS = 3;

const UP = new THREE.Vector3(0, 1, 0);

/**
 * The cone of light between the lens and the screen: four triangles from the
 * lens to the four corners of the picture, and nothing else.
 *
 * Built in the picture's own frame rather than in world space, because that is
 * what lets it be a child of the picture - the image is the rectangle at z = 0,
 * so undoing the plane's yaw is the whole of the transform. There is no cap at
 * either end: the picture itself is the far one, and at the near one four
 * triangles meeting at a point is exactly the flare a lens has.
 *
 * Additive and depth-tested but not depth-written, which is the whole trick of
 * a light shaft. Additive because light adds - two faces of the cone seen
 * through each other are brighter than one, and that is why the beam has a hot
 * core without anything being drawn there. Depth-tested so a table leg still
 * cuts it. Not depth-written so the far wall of the cone is not thrown away by
 * the near one. `DoubleSide` for the seat at the end of the run, which is
 * inside the beam - see the finish.
 */
function buildBeam(spec: ProjectionSpec, beam: BeamSpec): THREE.Mesh {
  const apex = new THREE.Vector3(
    beam.lens.x - spec.center.x,
    beam.lens.y - spec.center.y,
    beam.lens.z - spec.center.z,
  ).applyAxisAngle(UP, -spec.rotationY);

  const halfWidth = spec.width / 2;
  const halfHeight = spec.height / 2;
  // A centimetre in front of the image, on the same reasoning that puts the
  // image a centimetre in front of the sheet: two surfaces this close together
  // fighting for the same pixels is a shimmer along the edge of the picture.
  const front = 0.01;
  const corners = [
    [-halfWidth, -halfHeight],
    [halfWidth, -halfHeight],
    [halfWidth, halfHeight],
    [-halfWidth, halfHeight],
  ];

  const positions: number[] = [];
  const colors: number[] = [];
  const lit = new THREE.Color(beam.color);
  const far = lit.clone().multiplyScalar(beam.falloff);
  for (let i = 0; i < corners.length; i++) {
    const [ax, ay] = corners[i];
    const [bx, by] = corners[(i + 1) % corners.length];
    positions.push(apex.x, apex.y, apex.z, ax, ay, front, bx, by, front);
    colors.push(lit.r, lit.g, lit.b, far.r, far.g, far.b, far.r, far.g, far.b);
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

  const mesh = new THREE.Mesh(
    geometry,
    new THREE.MeshBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: beam.opacity,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
      fog: false,
      toneMapped: false,
    }),
  );
  mesh.name = `${spec.name}-keila`;
  return mesh;
}

export class Projection {
  readonly mesh: THREE.Mesh;

  private readonly beam: THREE.Mesh;

  private readonly video: HTMLVideoElement;
  private readonly texture: THREE.VideoTexture;
  private previousTime = 0;
  private suspended = false;
  private powerOn = true;
  /** Failed starts since the last frame actually arrived. See `MAX_RELOADS`. */
  private reloads = 0;
  /** Set by `dispose`, so the teardown's own pause is not undone by `onPause`. */
  private disposed = false;

  constructor(
    spec: ProjectionSpec,
    /** Seconds into the clip that the hall shouts. */
    private readonly cueAt: number,
  ) {
    this.video = document.createElement('video');
    this.video.src = clipUrl;
    this.video.loop = true;
    // Muted to begin with, which is the whole of what the autoplay policy
    // wants: the clip has a soundtrack, and it is unmuted by `setVolume` once
    // the player is somewhere it should be audible from - which is deep inside
    // a building they walked into, long after the click that let it start.
    this.video.muted = true;
    this.video.defaultMuted = true;
    this.video.playsInline = true;
    this.video.preload = 'auto';
    // Everything that keeps it running. Registered before the first `play()`
    // so a source that fails on the very first attempt is caught too.
    this.video.addEventListener('error', this.onStall);
    this.video.addEventListener('stalled', this.onStall);
    this.video.addEventListener('pause', this.onPause);
    this.video.addEventListener('playing', this.onPlaying);
    this.start();

    this.texture = new THREE.VideoTexture(this.video);
    this.texture.colorSpace = THREE.SRGBColorSpace;

    this.mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(spec.width, spec.height),
      // Unlit, and never fogged: a projected image is light leaving the wall,
      // so the morning's grey has no business dimming it.
      new THREE.MeshBasicMaterial({ map: this.texture, fog: false, toneMapped: false }),
    );
    this.mesh.position.set(spec.center.x, spec.center.y, spec.center.z);
    this.mesh.rotation.y = spec.rotationY;
    this.mesh.name = spec.name;

    // A child, so it needs no separate add, no separate visibility and no
    // second copy of where the screen is.
    this.beam = buildBeam(spec, spec.beam);
    this.mesh.add(this.beam);
  }

  /**
   * Advances nothing - the browser is playing the video - and answers the one
   * question the rest of the game has: did the clip just pass its cue?
   *
   * Called once a frame from `render`, and returning a bool rather than firing
   * the sound itself keeps three.js and the mixer from having to know about
   * each other.
   */
  update(): boolean {
    const now = this.video.currentTime;
    // A playhead that is not moving cannot cross anything, so a paused menu
    // and a dead projector are both covered by asking whether it is playing.
    const running = this.video.readyState > 0 && !this.video.paused;
    const fired = running && crossedCue(this.previousTime, now, this.cueAt);
    this.previousTime = now;
    return fired;
  }

  /**
   * How loud the clip's own soundtrack is, 0 - 1, decided per frame by where
   * the player is standing (`PROJECTOR` in src/config/audio.ts).
   *
   * The sound stays *inside the video element* rather than being handed to the
   * mixer, and that is the point: a gunshot has to land on the frame that fired
   * it, and one clock owning both the picture and the audio is the only way
   * that stays true through a decoder's own stalls. What the mixer's arithmetic
   * still decides is the level - `placedMix` walks this down through the
   * navetta's wall exactly as it does the karaoke, so the two mix rather than
   * fight.
   *
   * Muted rather than merely turned down at zero: it is being muted that the
   * autoplay policy cares about, so out in the yard - and before anybody has
   * clicked - the clip keeps playing on the terms it was allowed to start on.
   */
  setVolume(gain: number): void {
    const level = Math.min(Math.max(gain, 0), 1);
    this.video.volume = level;
    this.video.muted = level <= 0;
  }

  /** Pause the picture with the rest of the game, for the pause menu. */
  setSuspended(suspended: boolean): void {
    if (suspended === this.suspended) return;
    this.suspended = suspended;
    this.sync();
  }

  /**
   * The projector is on the venue's mains like everything else with a plug in
   * it, so the cable coming out of the tolppa takes the picture with it - and
   * takes the shout that goes with the picture, because a playhead that is not
   * moving never reaches fifteen seconds. See `World.setPowerOn`.
   */
  setPowerOn(on: boolean): void {
    if (on === this.powerOn) return;
    this.powerOn = on;
    this.mesh.visible = on;
    this.sync();
  }

  /** Plays only when the game is running and the building has power. */
  private sync(): void {
    if (this.suspended || !this.powerOn) this.video.pause();
    else this.start();
  }

  dispose(): void {
    this.disposed = true;
    for (const event of UNLOCK_EVENTS) window.removeEventListener(event, this.onUserGesture);
    this.video.removeEventListener('error', this.onStall);
    this.video.removeEventListener('stalled', this.onStall);
    this.video.removeEventListener('pause', this.onPause);
    this.video.removeEventListener('playing', this.onPlaying);
    this.video.pause();
    this.video.removeAttribute('src');
    this.video.load();
    this.texture.dispose();
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
    this.beam.geometry.dispose();
    (this.beam.material as THREE.Material).dispose();
  }

  /**
   * Muted autoplay is allowed everywhere that matters, but a browser told to
   * save data - or a Safari with the setting off - will refuse it anyway. So
   * the refusal is caught, and the first thing the player does to the page is
   * taken as permission to try once more. Nothing waits on any of this: the
   * screen simply shows its first frame until it starts.
   */
  private start(): void {
    void this.video.play().catch(() => {
      for (const event of UNLOCK_EVENTS) {
        window.addEventListener(event, this.onUserGesture, { once: true, passive: true });
      }
    });
  }

  private readonly onUserGesture = (): void => {
    for (const event of UNLOCK_EVENTS) window.removeEventListener(event, this.onUserGesture);
    this.sync();
  };

  /**
   * A frame arrived, so whatever was wrong is over and the budget is refilled.
   */
  private readonly onPlaying = (): void => {
    this.reloads = 0;
  };

  /**
   * The clip stopped arriving, or never started.
   *
   * `stalled` is also what a decoder that is merely behind fires, and that one
   * comes back on its own - a `load()` on top of it would throw away what it
   * had buffered and start the wait again. So the test is `readyState`: not one
   * frame of metadata means nothing has arrived at all, and that is the case
   * worth starting over.
   */
  private readonly onStall = (): void => {
    if (this.video.error === null && this.video.readyState > 0) return;
    if (this.suspended || !this.powerOn || this.reloads >= MAX_RELOADS) return;
    this.reloads++;
    // `load()` rather than another `play()`: the element is holding a source it
    // has given up on, and only a fresh load will let go of it.
    this.video.load();
    this.start();
  };

  /**
   * Somebody paused it, and the only somebody allowed to is `sync`.
   *
   * Everything else - an OS interruption, a media session handed to another
   * tab, a decoder giving up quietly - would leave the wall showing one frame
   * for the rest of the run, which reads as a broken projector rather than as
   * a browser being careful. So it is put straight back.
   */
  private readonly onPause = (): void => {
    if (this.disposed || this.suspended || !this.powerOn) return;
    this.start();
  };
}
